import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as StaffManagementService from '_services/staff-management-service';

const initialState = {
    employeelist: [],
    total: 0,
    hasMore: false,
    page: 0,
    pageSize: 25,
    status: 'idle',
    error: null,
    inviteStatus: 'idle',
    inviteError: null,
    adminStatus: 'idle',
    adminError: null,
};

// fetch the staff list for the caller's organisation
export const fetchEmployeeList = createAsyncThunk('staffManagement/fetchEmployeeList', async ({ limit = 25, skip = 0, search = '' } = {}) => {
    return StaffManagementService.getEmployeeList({ limit, skip, search });
});

// invite a staff member
export const inviteEmployee = createAsyncThunk('staffManagement/inviteEmployee', async (inviteData) => {
    return StaffManagementService.inviteEmployee(inviteData);
});

// update a staff member's role
export const updateEmployeeRole = createAsyncThunk('staffManagement/updateEmployeeRole', async ({ employeeId, role }) => {
    return StaffManagementService.updateEmployeeRole(employeeId, role);
});

// remove a staff member
export const deleteEmployee = createAsyncThunk('staffManagement/deleteEmployee', async (employeeId) => {
    return StaffManagementService.deleteEmployee(employeeId);
});

// resend an invitation
export const resendInvitation = createAsyncThunk('staffManagement/resendInvitation', async (employeeId) => {
    return StaffManagementService.resendInvitation(employeeId);
});

// require MFA for an existing staff member
export const requireEmployeeMfa = createAsyncThunk('staffManagement/requireEmployeeMfa', async (employeeId) => {
    return StaffManagementService.requireEmployeeMfa(employeeId);
});

// send a password reset email to a staff member
export const sendPasswordReset = createAsyncThunk('staffManagement/sendPasswordReset', async (email) => {
    return StaffManagementService.sendPasswordReset(email);
});

// create a new organisation admin (super admin only)
export const createAdminAccount = createAsyncThunk('staffManagement/createAdminAccount', async (adminData) => {
    return StaffManagementService.createAdminAccount(adminData);
});

// confirm password changed (onboarding)
export const confirmPasswordChanged = createAsyncThunk('staffManagement/confirmPasswordChanged', async () => {
    return StaffManagementService.confirmPasswordChanged();
});

// confirm MFA completed (onboarding)
export const confirmMfaCompleted = createAsyncThunk('staffManagement/confirmMfaCompleted', async () => {
    return StaffManagementService.confirmMfaCompleted();
});

const staffManagementSlice = createSlice({
    name: 'staffManagement',
    initialState,
    reducers: {
        reset: (state) => initialState,
        resetStatus: (state) => {
            return {
                ...state,
                status: 'idle',
                error: null,
                inviteStatus: 'idle',
                inviteError: null,
                adminStatus: 'idle',
                adminError: null,
            }
        }
    },
    extraReducers: {
        [fetchEmployeeList.pending]: (state) => {
            return { ...state, status: 'loading', error: null }
        },
        [fetchEmployeeList.fulfilled]: (state, action) => {
            return {
                ...state,
                status: 'succeeded',
                employeelist: action.payload?.employees || [],
                total: action.payload?.total || 0,
                hasMore: action.payload?.hasMore || false,
                page: Math.floor((action.payload?.skip || 0) / (action.payload?.limit || 25)),
                pageSize: action.payload?.limit || 25,
            }
        },
        [fetchEmployeeList.rejected]: (state, action) => {
            return { ...state, status: 'failed', error: action.error.message }
        },
        [inviteEmployee.pending]: (state) => {
            return { ...state, inviteStatus: 'loading', inviteError: null }
        },
        [inviteEmployee.fulfilled]: (state) => {
            return { ...state, inviteStatus: 'succeeded' }
        },
        [inviteEmployee.rejected]: (state, action) => {
            return { ...state, inviteStatus: 'failed', inviteError: action.error.message }
        },
        [updateEmployeeRole.pending]: (state) => {
            return { ...state, status: 'loading', error: null }
        },
        [updateEmployeeRole.fulfilled]: (state, action) => {
            return {
                ...state,
                status: 'succeeded',
                employeelist: state.employeelist.map(employee =>
                    employee.id === action.payload.id
                        ? { ...employee, role: action.payload.role }
                        : employee
                ),
            }
        },
        [updateEmployeeRole.rejected]: (state, action) => {
            return { ...state, status: 'failed', error: action.error.message }
        },
        [deleteEmployee.pending]: (state) => {
            return { ...state, status: 'loading', error: null }
        },
        [deleteEmployee.fulfilled]: (state, action) => {
            return {
                ...state,
                status: 'succeeded',
                employeelist: state.employeelist.filter(employee => employee.id !== action.payload),
            }
        },
        [deleteEmployee.rejected]: (state, action) => {
            return { ...state, status: 'failed', error: action.error.message }
        },
        [resendInvitation.pending]: (state) => {
            return { ...state, status: 'loading', error: null }
        },
        [resendInvitation.fulfilled]: (state) => {
            return { ...state, status: 'succeeded' }
        },
        [resendInvitation.rejected]: (state, action) => {
            return { ...state, status: 'failed', error: action.error.message }
        },
        [requireEmployeeMfa.pending]: (state) => {
            return { ...state, status: 'loading', error: null }
        },
        [requireEmployeeMfa.fulfilled]: (state, action) => {
            const employeeId = action.payload?.data?.firebaseId;
            const mfaRequired = action.payload?.data?.mfaRequired === true;
            const passwordResetRequired = action.payload?.data?.passwordResetRequired === true;
            const alreadyEnrolled = action.payload?.data?.alreadyEnrolled === true;
            return {
                ...state,
                status: 'succeeded',
                employeelist: state.employeelist.map(employee =>
                    employee.id === employeeId
                        ? {
                            ...employee,
                            mfaRequired: mfaRequired,
                            mfaEnrolled: alreadyEnrolled || employee.mfaEnrolled,
                            pending: passwordResetRequired || employee.pending,
                        }
                        : employee
                ),
            }
        },
        [requireEmployeeMfa.rejected]: (state, action) => {
            return { ...state, status: 'failed', error: action.error.message }
        },
        [createAdminAccount.pending]: (state) => {
            return { ...state, adminStatus: 'loading', adminError: null }
        },
        [createAdminAccount.fulfilled]: (state) => {
            return { ...state, adminStatus: 'succeeded' }
        },
        [createAdminAccount.rejected]: (state, action) => {
            return { ...state, adminStatus: 'failed', adminError: action.error.message }
        },
        [sendPasswordReset.pending]: (state) => {
            return { ...state, status: 'loading', error: null }
        },
        [sendPasswordReset.fulfilled]: (state) => {
            return { ...state, status: 'succeeded' }
        },
        [sendPasswordReset.rejected]: (state, action) => {
            return { ...state, status: 'failed', error: action.error.message }
        },
        [confirmPasswordChanged.pending]: (state) => {
            return { ...state, status: 'loading', error: null }
        },
        [confirmPasswordChanged.fulfilled]: (state) => {
            return { ...state, status: 'succeeded' }
        },
        [confirmPasswordChanged.rejected]: (state, action) => {
            return { ...state, status: 'failed', error: action.error.message }
        },
        [confirmMfaCompleted.pending]: (state) => {
            return { ...state, status: 'loading', error: null }
        },
        [confirmMfaCompleted.fulfilled]: (state) => {
            return { ...state, status: 'succeeded' }
        },
        [confirmMfaCompleted.rejected]: (state, action) => {
            return { ...state, status: 'failed', error: action.error.message }
        },
    }
});

export const { reset, resetStatus } = staffManagementSlice.actions;

export const selectEmployeeList = state => state.staffManagement.employeelist;
export const selectStaffManagementStatus = state => state.staffManagement.status;
export const selectStaffManagementError = state => state.staffManagement.error;
export const selectInviteStatus = state => state.staffManagement.inviteStatus;
export const selectInviteError = state => state.staffManagement.inviteError;
export const selectAdminStatus = state => state.staffManagement.adminStatus;
export const selectAdminError = state => state.staffManagement.adminError;
export const selectStaffTotal = state => state.staffManagement.total;
export const selectStaffHasMore = state => state.staffManagement.hasMore;
export const selectStaffPage = state => state.staffManagement.page;
export const selectStaffPageSize = state => state.staffManagement.pageSize;

export default staffManagementSlice.reducer;
